/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * Route-level code splitting with one safety net: after a deploy the hashed
 * chunks of the previous build disappear from S3, so a tab that still holds
 * the old `index.html` gets a `ChunkLoadError` when it navigates to a route it
 * has not visited yet. On that error we reload the page once (flag in
 * sessionStorage) so the browser picks the new `index.html`; a second failure
 * surfaces normally.
 */
const RELOAD_FLAG = 'chunk-reload-attempted';

const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: string; message?: string };
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message ?? '') ||
    /Failed to fetch dynamically imported module/i.test(message ?? '')
  );
};

const readFlag = (): boolean => {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (value: boolean): void => {
  try {
    if (value) window.sessionStorage.setItem(RELOAD_FLAG, '1');
    else window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // Storage unavailable: fall through to the plain error.
  }
};

const withRetry = <T>(factory: () => Promise<T>): Promise<T> =>
  factory()
    .then((module) => {
      writeFlag(false);
      return module;
    })
    .catch((error: unknown) => {
      if (isChunkLoadError(error) && !readFlag()) {
        writeFlag(true);
        window.location.reload();
        // Never resolves: the page is reloading.
        return new Promise<T>(() => {
          /* intentionally pending */
        });
      }
      throw error;
    });

/** `React.lazy` for a module with a default export. */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => withRetry(factory));
}

/** `React.lazy` for a named export, e.g. `lazyNamed(() => import('./pages/Home'), 'Home')`. */
export function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(
  factory: () => Promise<M>,
  name: K
): LazyExoticComponent<
  M[K] extends ComponentType<any> ? M[K] : ComponentType<any>
> {
  return lazy(() =>
    withRetry(factory).then((module) => ({
      default: module[name] as ComponentType<any>,
    }))
  ) as LazyExoticComponent<
    M[K] extends ComponentType<any> ? M[K] : ComponentType<any>
  >;
}
